import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Save } from "lucide-react";

/**
 * Reflection widget shown after a lesson completes.
 * Prompts: what I learned, how I learned it, what to work on next.
 */
export default function ReflectionCard({ lessonId }) {
  const [existing, setExisting] = useState([]);
  const [learned, setLearned] = useState("");
  const [how, setHow] = useState("");
  const [nextFocus, setNextFocus] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/reflections?lesson_id=${lessonId}`).then(({ data }) => setExisting(data)).catch(() => {});
  }, [lessonId]);

  const save = async () => {
    if (!learned.trim()) return toast.error("Write at least one thing you learned.");
    setSaving(true);
    try {
      const { data } = await api.post("/reflections", { lesson_id: lessonId, learned, how_learned: how, next_focus: nextFocus });
      setExisting((prev) => [data, ...prev]);
      setLearned(""); setHow(""); setNextFocus("");
      toast.success("Reflection saved · +5 points");
    } catch { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <section className="p-6 rounded-3xl bg-sky-50 border border-sky-100 space-y-3" data-testid="reflection-card">
      <div className="flex items-center gap-2 text-sky-800"><Sparkles size={16} /> <h3 className="font-display text-lg font-semibold">Quick reflection</h3></div>
      <p className="text-sm text-slate-600">No wrong answers. Two sentences is plenty.</p>

      <div>
        <label className="text-sm font-medium text-slate-700">One thing I learned</label>
        <textarea data-testid="ref-learned" rows={2} value={learned} onChange={(e)=>setLearned(e.target.value)}
          className="mt-1 w-full px-4 py-3 rounded-xl border border-sky-200 bg-white focus:border-teal-500 outline-none" />
      </div>
      <div>
        <label className="text-sm font-medium text-slate-700">How I understood it (video, practice, trying it, etc.)</label>
        <textarea data-testid="ref-how" rows={2} value={how} onChange={(e)=>setHow(e.target.value)}
          className="mt-1 w-full px-4 py-3 rounded-xl border border-sky-200 bg-white focus:border-teal-500 outline-none" />
      </div>
      <div>
        <label className="text-sm font-medium text-slate-700">What I want to work on next</label>
        <textarea data-testid="ref-next" rows={2} value={nextFocus} onChange={(e)=>setNextFocus(e.target.value)}
          className="mt-1 w-full px-4 py-3 rounded-xl border border-sky-200 bg-white focus:border-teal-500 outline-none" />
      </div>
      <button data-testid="ref-save" onClick={save} disabled={saving}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold disabled:opacity-60">
        <Save size={14} /> {saving ? "Saving…" : "Save reflection"}
      </button>

      {existing.length > 0 && (
        <div className="mt-4 pt-4 border-t border-sky-200/60 space-y-2 text-sm">
          <div className="text-xs text-slate-500 uppercase tracking-widest">Previous</div>
          {existing.slice(0, 3).map((r) => (
            <div key={r.id} className="p-3 rounded-xl bg-white border border-sky-100">
              <div className="text-slate-800"><b>Learned:</b> {r.learned}</div>
              {r.next_focus && <div className="text-slate-600 mt-1"><b>Next:</b> {r.next_focus}</div>}
              <div className="text-[11px] text-slate-400 mt-1">{new Date(r.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
